import { FC, ReactElement } from 'react';
import { Box, SxProps, Theme, Tooltip } from '@mui/material';
import { WarningAlert } from 'ui/WarningAlert';

// The operator's messages are plain text with line breaks: rendered as text (never HTML) with pre-line.
const PRE_LINE = { whiteSpace: 'pre-line' } as const;

type TClientAccessBannerProps = {
	/** Messages of the blocked scopes (`null`: the scope is allowed). The same text is shown once. */
	messages: (string | null | undefined)[];
	sx?: SxProps<Theme>;
};

/** Banner with the operator's message while payment methods / payments are blocked (api/client-access). */
export const ClientAccessBanner: FC<TClientAccessBannerProps> = ({ messages, sx }) => {
	const unique = Array.from(new Set(messages.filter((message): message is string => !!message)));
	if (!unique.length) return null;

	return (
		<Box sx={sx} role="status">
			{unique.map((message) => (
				<WarningAlert key={message} sx={{ ...PRE_LINE, mt: 0, mb: 1 }}>
					{message}
				</WarningAlert>
			))}
		</Box>
	);
};

type TBlockedTooltipProps = {
	/** The operator's message; `null`: the action is allowed and the child is rendered as is. */
	message: string | null;
	children: ReactElement;
};

/** Tooltip with the operator's message over a disabled control (a disabled button fires no events: wrapped in a span). */
export const BlockedTooltip: FC<TBlockedTooltipProps> = ({ message, children }) => {
	if (!message) return children;

	return (
		<Tooltip title={message} arrow componentsProps={{ tooltip: { sx: PRE_LINE } }}>
			<Box component="span" sx={{ display: 'inline-flex' }}>
				{children}
			</Box>
		</Tooltip>
	);
};
